import type { ResearchLineageNodeProjection, ResearchNodeRef } from "@roboscientist/contracts";

export const lineageNodeDiameter = 20;
export const lineageInspectorDefaultWidth = 330;
export const lineageInspectorMinimumWidth = 280;
export const lineageInspectorMaximumWidth = 720;
export const lineageCanvasPaneMinimumWidth = 460;
export const lineageResizeHandleWidth = 12;
export const lineageLaneLabelWidth = 142;
const canvasTopPadding = 42;
const canvasBottomPadding = 18;
const canvasRightPadding = 80;
const minimumCanvasLaneCount = 10;
const defaultViewportWidth = 720;
const expandedViewportWidth = 1_400;
const minimumSpacingScale = .5;
const defaultSpacingScale = 2 / 3;
const maximumSpacingScale = 1;
const baseGenerationNodePitch = 32;
const baseGenerationGap = 40;
const baseMaximumResponsiveGenerationGap = 84;
const baseLaneGap = 6;
const baseMinimumLaneHeight = 36;
const baseMinimumGenerationWidth = 76;
const baseGenerationInnerPadding = 24;
const scaledSpacing = (value: number, scale: number): number => value * scale;
const normalizedViewportWidth = (viewportWidth?: number): number =>
  typeof viewportWidth === "number" && Number.isFinite(viewportWidth) && viewportWidth > 0
    ? viewportWidth
    : defaultViewportWidth;
const viewportSpan = expandedViewportWidth - lineageCanvasPaneMinimumWidth;
const defaultViewportProgress = (defaultViewportWidth - lineageCanvasPaneMinimumWidth) / viewportSpan;
const scaleSpan = maximumSpacingScale - minimumSpacingScale;
const defaultScaleDelta = defaultSpacingScale - minimumSpacingScale;
const scaleCurve = (defaultScaleDelta - scaleSpan * defaultViewportProgress)
  / (defaultViewportProgress * (defaultViewportProgress - 1));
const scaleSlope = scaleSpan - scaleCurve;
const contentFitSearchIterations = 20;

export function lineageHorizontalSpacingScaleForViewport(viewportWidth?: number): number {
  const width = normalizedViewportWidth(viewportWidth);
  const progress = Math.max(0, Math.min(
    1,
    (width - lineageCanvasPaneMinimumWidth) / viewportSpan
  ));
  return minimumSpacingScale + scaleCurve * progress * progress + scaleSlope * progress;
}

/**
 * The viewport curve is a comfort ceiling. Dense content may reduce it so all
 * formal nodes fit, but never below the minimum readable spacing.
 */
const lineageHorizontalSpacingScaleForContent = (
  viewportWidth: number,
  preferredScale: number,
  naturalWidthAtScale: (scale: number) => number
): number => {
  if (naturalWidthAtScale(preferredScale) <= viewportWidth) return preferredScale;
  if (naturalWidthAtScale(minimumSpacingScale) >= viewportWidth) return minimumSpacingScale;

  let fittingScale = minimumSpacingScale;
  let overflowingScale = preferredScale;
  for (let iteration = 0; iteration < contentFitSearchIterations; iteration += 1) {
    const candidateScale = (fittingScale + overflowingScale) / 2;
    if (naturalWidthAtScale(candidateScale) <= viewportWidth) {
      fittingScale = candidateScale;
    } else {
      overflowingScale = candidateScale;
    }
  }
  return fittingScale;
};

const defaultLaneGap = scaledSpacing(baseLaneGap, defaultSpacingScale);
const defaultMinimumLaneHeight = scaledSpacing(baseMinimumLaneHeight, defaultSpacingScale);
export const lineageMinimumCanvasHeight = canvasTopPadding
  + minimumCanvasLaneCount * (defaultMinimumLaneHeight + defaultLaneGap)
  - defaultLaneGap
  + canvasBottomPadding;

export function clampLineageInspectorWidth(requestedWidth: number, workspaceWidth: number): number {
  const requested = Number.isFinite(requestedWidth) ? requestedWidth : lineageInspectorDefaultWidth;
  const workspaceMaximum = Number.isFinite(workspaceWidth) && workspaceWidth > 0
    ? workspaceWidth - lineageCanvasPaneMinimumWidth - lineageResizeHandleWidth
    : lineageInspectorMaximumWidth;
  const maximum = Math.max(
    lineageInspectorMinimumWidth,
    Math.min(lineageInspectorMaximumWidth, workspaceMaximum)
  );
  return Math.round(Math.min(maximum, Math.max(lineageInspectorMinimumWidth, requested)));
}

export type LineageLayoutNode = {
  key: string;
  ref: ResearchNodeRef;
  x: number;
  y: number;
  depth: number;
  directionFamily: string;
  laneCenterY: number;
};

export type LineageDirectionLane = {
  id: string;
  directionFamily: string;
  y: number;
  height: number;
  centerY: number;
  nodeCount: number;
};

export type LineageGenerationBand = {
  depth: number;
  x: number;
  width: number;
  centerX: number;
};

export type LineageLayoutEdge = {
  id: string;
  parentKey: string;
  childKey: string;
  secondary: boolean;
  curveOffset: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export type ResearchLineageLayout = {
  width: number;
  height: number;
  maxDepth: number;
  generations: LineageGenerationBand[];
  lanes: LineageDirectionLane[];
  nodes: LineageLayoutNode[];
  edges: LineageLayoutEdge[];
};

export type ResearchLineageLayoutOptions = {
  viewportWidth?: number;
};

export const lineageRefKey = (reference: ResearchNodeRef): string =>
  `${reference.proposalId}\u0000${reference.experimentId}\u0000${reference.nodeId}`;

export const lineageNodeDomId = (key: string): string =>
  `lineage-node-${encodeURIComponent(key)}`;

export const lineageNodeIsPruned = (node: ResearchLineageNodeProjection): boolean =>
  node.currentSelection?.state === "deprioritized" || node.currentSelection?.state === "stopped";

export function lineagePrimaryChildCounts(nodes: ResearchLineageNodeProjection[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of nodes) {
    if (!node.primaryParentRef) continue;
    const parentKey = lineageRefKey(node.primaryParentRef);
    counts.set(parentKey, (counts.get(parentKey) ?? 0) + 1);
  }
  return counts;
}

const directionPathKey = (path: string[]): string => path.join("\u0000");

const directionFamilyOf = (node: ResearchLineageNodeProjection): string =>
  node.directionPath[0]?.trim() || "Needs classification";

/**
 * A visual Fork is a real hypothesis/direction divergence, not merely two Runs
 * or refinements that happen to share a parent.
 */
export function lineageForkChildCounts(nodes: ResearchLineageNodeProjection[]): Map<string, number> {
  const byKey = new Map(nodes.map((node) => [lineageRefKey(node.ref), node]));
  const childrenByParent = new Map<string, ResearchLineageNodeProjection[]>();
  for (const child of nodes) {
    if (!child.primaryParentRef) continue;
    const parentKey = lineageRefKey(child.primaryParentRef);
    if (!byKey.has(parentKey)) continue;
    childrenByParent.set(parentKey, [...(childrenByParent.get(parentKey) ?? []), child]);
  }
  const forks = new Map<string, number>();
  for (const [parentKey, children] of childrenByParent) {
    if (children.length < 2) continue;
    const parentDirection = directionPathKey(byKey.get(parentKey)?.directionPath ?? []);
    const childDirections = new Set(children.map((child) => directionPathKey(child.directionPath)));
    if (childDirections.size > 1 || !childDirections.has(parentDirection)) {
      forks.set(parentKey, children.length);
    }
  }
  return forks;
}

export function layoutResearchLineage(
  nodes: ResearchLineageNodeProjection[],
  options: ResearchLineageLayoutOptions = {}
): ResearchLineageLayout {
  const viewportWidth = normalizedViewportWidth(options.viewportWidth);
  const laneGap = defaultLaneGap;
  const minimumLaneHeight = defaultMinimumLaneHeight;
  const byKey = new Map(nodes.map((node) => [lineageRefKey(node.ref), node]));
  const depths = new Map<string, number>();
  const visiting = new Set<string>();
  const depthOf = (key: string): number => {
    const cached = depths.get(key);
    if (cached !== undefined) return cached;
    if (visiting.has(key)) return 0;
    const node = byKey.get(key);
    if (!node) return 0;
    visiting.add(key);
    const parents = [
      ...(node.primaryParentRef ? [node.primaryParentRef] : []),
      ...node.secondaryParentRefs
    ].map(lineageRefKey).filter((parentKey) => byKey.has(parentKey));
    const depth = parents.length ? Math.max(...parents.map(depthOf)) + 1 : 0;
    visiting.delete(key);
    depths.set(key, depth);
    return depth;
  };
  for (const key of byKey.keys()) depthOf(key);
  const maxDepth = Math.max(0, ...depths.values());

  const nodesByDirection = new Map<string, ResearchLineageNodeProjection[]>();
  const countsByDirectionDepth = new Map<string, number>();
  for (const node of nodes) {
    const directionFamily = directionFamilyOf(node);
    nodesByDirection.set(directionFamily, [...(nodesByDirection.get(directionFamily) ?? []), node]);
    const depth = depths.get(lineageRefKey(node.ref)) ?? 0;
    const countKey = `${directionFamily}\u0000${depth}`;
    countsByDirectionDepth.set(countKey, (countsByDirectionDepth.get(countKey) ?? 0) + 1);
  }

  const largestDirectionGroups = Array.from({ length: maxDepth + 1 }, (_, depth) =>
    Math.max(1, ...[...nodesByDirection.keys()].map((directionFamily) =>
      countsByDirectionDepth.get(`${directionFamily}\u0000${depth}`) ?? 0)));
  const generationNodePitchAtScale = (scale: number): number =>
    Math.max(lineageNodeDiameter, scaledSpacing(baseGenerationNodePitch, scale));
  const generationWidthAtScale = (largestDirectionGroup: number, scale: number): number => {
    const generationNodePitch = generationNodePitchAtScale(scale);
    const minimumGenerationWidth = scaledSpacing(baseMinimumGenerationWidth, scale);
    const generationInnerPadding = scaledSpacing(baseGenerationInnerPadding, scale);
    return Math.max(
      minimumGenerationWidth,
      lineageNodeDiameter + generationInnerPadding
        + (largestDirectionGroup - 1) * generationNodePitch
    );
  };
  const generationWidthsAtScale = (scale: number): number[] =>
    largestDirectionGroups.map((largestDirectionGroup) =>
      generationWidthAtScale(largestDirectionGroup, scale));
  const naturalWidthAtScale = (scale: number): number => lineageLaneLabelWidth
    + largestDirectionGroups.reduce((total, largestDirectionGroup) =>
      total + generationWidthAtScale(largestDirectionGroup, scale), 0)
    + Math.max(0, largestDirectionGroups.length - 1) * scaledSpacing(baseGenerationGap, scale)
    + canvasRightPadding;
  const preferredHorizontalSpacingScale = lineageHorizontalSpacingScaleForViewport(options.viewportWidth);
  const horizontalSpacingScale = lineageHorizontalSpacingScaleForContent(
    viewportWidth,
    preferredHorizontalSpacingScale,
    naturalWidthAtScale
  );
  const generationNodePitch = generationNodePitchAtScale(horizontalSpacingScale);
  const generationGap = scaledSpacing(baseGenerationGap, horizontalSpacingScale);
  const maximumResponsiveGenerationGap = scaledSpacing(
    baseMaximumResponsiveGenerationGap,
    horizontalSpacingScale
  );
  const generationWidths = generationWidthsAtScale(horizontalSpacingScale);
  const generationsWidth = generationWidths.reduce((total, width) => total + width, 0);
  const naturalWidth = naturalWidthAtScale(horizontalSpacingScale);
  const targetWidth = Math.max(naturalWidth, viewportWidth);
  const responsiveGenerationGap = generationWidths.length > 1
    ? Math.min(
      maximumResponsiveGenerationGap,
      Math.max(
        generationGap,
        (targetWidth - lineageLaneLabelWidth - canvasRightPadding - generationsWidth) / (generationWidths.length - 1)
      )
    )
    : generationGap;
  const generations: LineageGenerationBand[] = [];
  let generationLeft = lineageLaneLabelWidth;
  generationWidths.forEach((width, depth) => {
    generations.push({ depth, x: generationLeft, width, centerX: generationLeft + width / 2 });
    generationLeft += width + responsiveGenerationGap;
  });
  const generationByDepth = new Map(generations.map((generation) => [generation.depth, generation]));

  const orderedDirections = [...nodesByDirection].sort(([leftId], [rightId]) => {
    if (leftId === "Baseline") return -1;
    if (rightId === "Baseline") return 1;
    if (leftId === "Needs classification") return 1;
    if (rightId === "Needs classification") return -1;
    return leftId.localeCompare(rightId);
  });
  const positioned: LineageLayoutNode[] = [];
  const directionLanes: LineageDirectionLane[] = [];
  let laneTop = canvasTopPadding;
  for (const [directionFamily, laneNodes] of orderedDirections) {
    const laneHeight = minimumLaneHeight;
    const laneCenterY = laneTop + laneHeight / 2;
    directionLanes.push({
      id: directionFamily,
      directionFamily,
      y: laneTop,
      height: laneHeight,
      centerY: laneCenterY,
      nodeCount: laneNodes.length
    });
    const columns = new Map<number, ResearchLineageNodeProjection[]>();
    for (const node of laneNodes) {
      const depth = depths.get(lineageRefKey(node.ref)) ?? 0;
      columns.set(depth, [...(columns.get(depth) ?? []), node]);
    }
    for (const [depth, column] of [...columns].sort(([left], [right]) => left - right)) {
      column.sort((left, right) => left.directionPath.join("/").localeCompare(right.directionPath.join("/"))
        || Date.parse(left.createdAt) - Date.parse(right.createdAt)
        || lineageRefKey(left.ref).localeCompare(lineageRefKey(right.ref)));
      const generation = generationByDepth.get(depth)!;
      column.forEach((node, index) => positioned.push({
        key: lineageRefKey(node.ref),
        ref: node.ref,
        depth,
        directionFamily,
        laneCenterY,
        x: generation.centerX + (index - (column.length - 1) / 2) * generationNodePitch - lineageNodeDiameter / 2,
        y: laneCenterY - lineageNodeDiameter / 2
      }));
    }
    laneTop += laneHeight + laneGap;
  }

  const positionByKey = new Map(positioned.map((node) => [node.key, node]));
  const edges: LineageLayoutEdge[] = [];
  for (const child of nodes) {
    const childPosition = positionByKey.get(lineageRefKey(child.ref));
    if (!childPosition) continue;
    const parents = [
      ...(child.primaryParentRef ? [{ ref: child.primaryParentRef, secondary: false }] : []),
      ...child.secondaryParentRefs.map((ref) => ({ ref, secondary: true }))
    ];
    for (const parent of parents) {
      const parentKey = lineageRefKey(parent.ref);
      const parentPosition = positionByKey.get(parentKey);
      if (!parentPosition) continue;
      edges.push({
        id: `${parentKey}->${childPosition.key}:${parent.secondary ? "secondary" : "primary"}`,
        parentKey,
        childKey: childPosition.key,
        secondary: parent.secondary,
        curveOffset: parent.secondary ? 12 : 0,
        x1: parentPosition.x + lineageNodeDiameter,
        y1: parentPosition.y + lineageNodeDiameter / 2,
        x2: childPosition.x,
        y2: childPosition.y + lineageNodeDiameter / 2
      });
    }
  }
  const primaryEdgesByParent = new Map<string, LineageLayoutEdge[]>();
  for (const edge of edges) {
    if (edge.secondary) continue;
    primaryEdgesByParent.set(edge.parentKey, [...(primaryEdgesByParent.get(edge.parentKey) ?? []), edge]);
  }
  for (const siblingEdges of primaryEdgesByParent.values()) {
    if (siblingEdges.length < 2) continue;
    siblingEdges.sort((left, right) => left.x2 - right.x2 || left.childKey.localeCompare(right.childKey));
    const maxArc = Math.min(13, 7 + siblingEdges.length);
    siblingEdges.forEach((edge, index) => {
      edge.curveOffset = -maxArc + index * (maxArc * 2 / (siblingEdges.length - 1));
    });
  }

  return {
    width: targetWidth,
    height: Math.max(lineageMinimumCanvasHeight, laneTop - laneGap + canvasBottomPadding),
    maxDepth,
    generations,
    lanes: directionLanes,
    nodes: positioned,
    edges
  };
}
